import cn from 'classnames';
import { isEmpty } from 'ramda';
import { useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { Skeleton } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import {
  PatientsFilter as PatientsFiltersEnum,
  PatientsFiltersType,
} from '@/entities/patient';
import { userModel } from '@/entities/user';

import { Filters } from '@/features/filters';

import styles from './PatientsFilters.module.scss';

type PatientsFiltersProps = {
  className?: string;
  amount: {
    sharedWithMeCount: number;
    sharedByMeCount: number;
    totalOrganizationPatientCount: number;
  };
  onFilterChange: (searchValue: string) => void;
};

export const PatientsFilters = ({
  onFilterChange,
  amount,
  className,
}: PatientsFiltersProps) => {
  const { sharedWithMeCount, sharedByMeCount, totalOrganizationPatientCount } =
    amount;

  const { formatMessage } = useIntl();

  const allOrganizations = useAppSelector(
    organizationModel.selectors.organizationSelectors.selectAll,
  );

  const organizationLoading = useAppSelector(
    (state) => state.organization.loading,
  );

  const showSkeleton =
    organizationLoading === 'pending' || organizationLoading === 'idle';

  const selectUserOrganizationLegacy = useAppSelector(
    userModel.selectors.selectUserOrganizationLegacy,
  );

  const { isCurrentUserAdmin } = selectUserOrganizationLegacy;

  const isForbidSharing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_Sharing,
    ),
  );

  const isAllOrganizations = !isEmpty(allOrganizations);

  const filters = {
    all: {
      value: PatientsFiltersEnum.all,
      label: formatMessage({ id: 'global.all', defaultMessage: 'All' }),
      amount: totalOrganizationPatientCount,
    },
    sharedWithMe: {
      value: PatientsFiltersEnum.sharedWithMe,
      label: formatMessage({
        id: 'patientList.filters.sharedWithMe',
        defaultMessage: 'Shared with me',
      }),
      amount: sharedWithMeCount,
    },
    sharedByStaff: {
      value: PatientsFiltersEnum.sharedByStaff,
      label: formatMessage({
        id: 'patientList.filters.sharedByStaff',
        defaultMessage: 'Shared by staff',
      }),
      amount: sharedByMeCount,
    },
    sharedByMe: {
      value: PatientsFiltersEnum.sharedByMe,
      label: formatMessage({
        id: 'patientList.filters.sharedByMe',
        defaultMessage: 'Shared by me',
      }),
      amount: sharedByMeCount,
    },
  };

  const getFiltersConfig = () => {
    if (isForbidSharing && isAllOrganizations) {
      return [filters.all];
    }

    if (isForbidSharing) {
      return [];
    }

    if (!isAllOrganizations) {
      return [filters.sharedWithMe];
    }

    const sharedByFilter = isCurrentUserAdmin
      ? filters.sharedByStaff
      : filters.sharedByMe;
    return [filters.all, filters.sharedWithMe, sharedByFilter];
  };

  const filtersItemsConfig = getFiltersConfig();

  return (
    <>
      {showSkeleton ? (
        <Skeleton.Filter
          filtersQuantity={3}
          height={48}
          gap={8}
          borderRadius={12}
        />
      ) : (
        <Filters<PatientsFiltersType>
          className={cn(styles.container, className)}
          filterName="filters"
          defaultActiveKey={
            (filtersItemsConfig.length === 1 && filtersItemsConfig[0].value) ||
            PatientsFiltersEnum.all
          }
          items={filtersItemsConfig}
          onChange={(event) => onFilterChange(event)}
        />
      )}
    </>
  );
};
